// views/Geradoras/Analise/AnaliseView.tsx
//
// Aba "Análise" do módulo Geradoras — o que o chamado pede:
//   1. gráficos de potência e geração hora a hora;
//   2. comparação entre períodos, para saber se a usina gerou o esperado.
//
// Toda a agregação acontece no Postgres (RPCs get_geracao_series /
// _series_total / _resumo, migration 20260827235910). A tela nunca baixa
// leitura crua: 30 dias de curva horária de uma usina são 720 linhas aqui
// contra ~8.600 leituras, e o corte silencioso de 1000 linhas do PostgREST
// devolveria série truncada sem erro nenhum.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Zap, Gauge, Scale, TriangleAlert, RefreshCw, Sun, Calendar,
    BarChart3, GitCompareArrows, Ruler, Factory, Info, ChevronDown, Search,
} from 'lucide-react';
import LoadingSpinner from '../../../src/components/LoadingSpinner';
import { api } from '../../../src/services/api';
import { useAuth } from '../../../src/contexts/AuthContext';
import type { UnidadeGeradora, MetaGeradoraRow, ResumoGeracaoUsina, GeradoraSemReportar, PontoVsMetaRow } from '../../../types';
import { formatKwh, formatPower, getDisplayUsina } from '../geradorasShared';
import {
    janelaDoPreset, janelaComparacao, granularidadeSugerida, resumirSerie, compararResumos,
    kwhPorKwp, detectarSinais, mediana, alinharPorPosicao, rotuloBucket,
    resumirVsMeta, diasDaJanela, diasNoMes, capacidadeKwp,
    PRESET_LABEL,
    type Granularidade, type ModoComparacao, type ModoReferencia, type PresetPeriodo,
    type PontoSerie,
} from '../geradorasSeries';
import CurvaGeracao from './CurvaGeracao';
import RankingUsinas, { type LinhaRanking } from './RankingUsinas';
import EsperadoRealizado from './EsperadoRealizado';
import Interrupcoes from './Interrupcoes';

const TODAS = '__todas__';

/**
 * Usina pedida pela URL (`/geradoras?aba=analise&site=<site_id_api>`).
 *
 * O alerta "Geradora parada" da Vick aponta para cá com a usina na query — sem
 * isto a pessoa caía em "Todas as usinas" e tinha que reencontrar no seletor a
 * usina que o alerta acabou de nomear.
 *
 * Leitura manual do hash pelo mesmo motivo do GeradorasView: o app usa
 * HashRouter e a troca de aba mexe na URL por `history.replaceState`, fora do
 * router — `useSearchParams` não acompanharia.
 */
const siteDaUrl = (): string =>
    new URLSearchParams(window.location.hash.split('?')[1] || '').get('site') || TODAS;

const GRANULARIDADES: { v: Granularidade; label: string }[] = [
    { v: 'hora', label: 'Hora a hora' },
    { v: 'dia', label: 'Por dia' },
    { v: 'mes', label: 'Por mês' },
];

const COMPARACOES: { v: ModoComparacao; label: string }[] = [
    { v: 'nenhum', label: 'Não comparar' },
    { v: 'anterior', label: 'Período anterior' },
    { v: 'ano_anterior', label: 'Mesmo período do ano passado' },
];

const REFERENCIAS: { v: ModoReferencia; label: string; hint: string }[] = [
    { v: 'irmas', label: 'Usinas irmãs', hint: 'Mediana do kWh/kWp das outras usinas no mesmo período' },
    { v: 'propria', label: 'A própria usina', hint: 'Compara com ela mesma no período anterior' },
    { v: 'meta', label: 'Meta cadastrada', hint: 'kWh/kWp esperado por mês, em Config' },
    { v: 'ano_anterior', label: 'Ano passado', hint: 'Mesmo período do ano anterior' },
];

// ---------------- UI base ----------------

// Dropdown dos filtros. Mesmo desenho do CRM (KanbanBoard / ResponsibleFilter):
// bottom-sheet no mobile, painel flutuante no desktop, opção ativa em primary.
// O <select> nativo abria a lista do sistema operacional — fundo azul, sem dark
// mode e sem lugar para o `hint` de cada opção, que só existia como tooltip.
const Seletor = <T extends string>({ tag, icon, value, options, onChange }: {
    tag: string; icon: React.ReactNode; value: T;
    options: { v: T; label: string; hint?: string }[]; onChange: (v: T) => void;
}) => {
    const [aberto, setAberto] = useState(false);
    const [busca, setBusca] = useState('');
    const [aDireita, setADireita] = useState(false);
    const botao = useRef<HTMLButtonElement>(null);

    const comBusca = options.length > 8;
    const selecionada = options.find(o => o.v === value);

    const visiveis = useMemo(() => {
        const termo = busca.trim().toLowerCase();
        return termo ? options.filter(o => o.label.toLowerCase().includes(termo)) : options;
    }, [options, busca]);

    useEffect(() => {
        if (!aberto) return;
        const aoTeclar = (e: KeyboardEvent) => { if (e.key === 'Escape') setAberto(false); };
        window.addEventListener('keydown', aoTeclar);
        return () => window.removeEventListener('keydown', aoTeclar);
    }, [aberto]);

    const alternar = () => {
        // Os cinco chips ficam numa linha: o último encosta na borda direita e um
        // painel ancorado à esquerda sairia da tela. Escolhe o lado ao abrir.
        const r = botao.current?.getBoundingClientRect();
        setADireita(!!r && r.left + 272 > window.innerWidth);
        setBusca('');
        setAberto(a => !a);
    };

    return (
        <div className="relative">
            <button
                ref={botao}
                onClick={alternar}
                className={`flex items-center gap-2.5 pl-3 pr-2.5 py-2 text-left bg-white dark:bg-gray-800 border rounded-xl shadow-sm transition-all group
                    ${aberto
                        ? 'border-primary ring-2 ring-primary/10'
                        : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'}`}
            >
                <span className={`w-6 h-6 rounded-lg flex items-center justify-center shrink-0 transition-colors
                    ${aberto ? 'bg-primary/10 text-primary' : 'bg-gray-100 dark:bg-gray-700 text-gray-400 group-hover:text-primary'}`}>
                    {icon}
                </span>
                <span className="min-w-0">
                    <span className="block text-[9px] text-gray-400 dark:text-gray-500 font-bold uppercase tracking-wider leading-none mb-0.5">{tag}</span>
                    <span className="block text-sm font-semibold text-gray-800 dark:text-gray-200 truncate max-w-[13rem]">
                        {selecionada?.label ?? value}
                    </span>
                </span>
                <ChevronDown size={13} className={`text-gray-400 shrink-0 transition-transform duration-300 ${aberto ? 'rotate-180' : ''}`} />
            </button>

            {aberto && (
                <>
                    <div className="fixed inset-0 z-40 bg-black/30 md:bg-transparent" onClick={() => setAberto(false)}></div>
                    <div className={`fixed bottom-0 left-0 right-0 md:absolute md:bottom-auto md:top-full ${aDireita ? 'md:left-auto md:right-0' : 'md:left-0 md:right-auto'} mt-0 md:mt-2 w-full md:w-64 bg-white dark:bg-gray-800 rounded-t-2xl md:rounded-2xl shadow-xl border border-gray-100 dark:border-gray-700 z-50 animate-fade-in flex flex-col max-h-[80vh] md:max-h-80`}>
                        <div className="w-10 h-1 bg-gray-300 dark:bg-gray-600 rounded-full mx-auto mt-3 mb-1 md:hidden shrink-0"></div>
                        <p className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider px-4 pt-2 pb-2 md:pt-3 md:pb-1 shrink-0">{tag}</p>

                        {comBusca && (
                            <div className="px-2 pb-2 shrink-0">
                                <div className="relative">
                                    <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                                    <input
                                        autoFocus
                                        value={busca}
                                        onChange={e => setBusca(e.target.value)}
                                        placeholder="Buscar..."
                                        className="w-full pl-7 pr-2 py-2 text-sm bg-gray-50 dark:bg-gray-700/60 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-700 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary/40"
                                    />
                                </div>
                            </div>
                        )}

                        <div className="overflow-y-auto px-2 pb-3 md:pb-2">
                            {visiveis.length === 0 ? (
                                <p className="px-3 py-5 text-sm text-gray-400 dark:text-gray-500 text-center">Nada encontrado</p>
                            ) : visiveis.map(o => (
                                <button
                                    key={o.v}
                                    onClick={() => { onChange(o.v); setAberto(false); }}
                                    className={`w-full text-left px-3 py-3 md:py-2.5 rounded-xl text-sm transition-all flex items-center justify-between gap-2 mb-1 last:mb-0
                                        ${o.v === value
                                            ? 'bg-primary/5 dark:bg-primary/10 text-primary font-bold'
                                            : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 font-medium'}`}
                                >
                                    <span className="min-w-0">
                                        <span className="block truncate">{o.label}</span>
                                        {o.hint && (
                                            <span className={`block text-[11px] font-normal leading-snug mt-0.5 whitespace-normal ${o.v === value ? 'text-primary/70' : 'text-gray-400 dark:text-gray-500'}`}>
                                                {o.hint}
                                            </span>
                                        )}
                                    </span>
                                    {o.v === value && <span className="w-2 h-2 rounded-full bg-primary shrink-0"></span>}
                                </button>
                            ))}
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

const Kpi = ({ titulo, valor, unidade, rodape, icone, tom }: {
    titulo: string; valor: string; unidade?: string; rodape: React.ReactNode;
    icone: React.ReactNode; tom: 'amber' | 'blue' | 'violet' | 'emerald' | 'rose';
}) => {
    const tons = {
        amber: 'bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400',
        blue: 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400',
        violet: 'bg-violet-50 dark:bg-violet-900/30 text-violet-600 dark:text-violet-400',
        emerald: 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400',
        rose: 'bg-rose-50 dark:bg-rose-900/30 text-rose-600 dark:text-rose-400',
    };
    return (
        <div className="bg-white dark:bg-gray-800 p-5 rounded-3xl border border-gray-100 dark:border-gray-700 shadow-sm">
            <div className="flex justify-between items-start gap-3">
                <div className="min-w-0">
                    <p className="text-[11px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">{titulo}</p>
                    <h3 className="text-2xl xl:text-3xl font-montserrat font-bold text-gray-900 dark:text-white tabular-nums truncate">
                        {valor}
                        {unidade && <span className="text-lg text-gray-400 font-semibold ml-1.5">{unidade}</span>}
                    </h3>
                </div>
                <div className={`p-3 rounded-2xl shadow-inner shrink-0 ${tons[tom]}`}>{icone}</div>
            </div>
            <div className="mt-4 text-xs text-gray-500 dark:text-gray-400 font-medium">{rodape}</div>
        </div>
    );
};

// ---------------- View ----------------

const AnaliseView: React.FC = () => {
    const { profile } = useAuth();
    // Mesmo público da policy pode_registrar_interrupcao_geradora: adm, gerente
    // ou quem tem o módulo — nunca o monitor externo. (`string` porque o tipo
    // UserRole não lista o papel do monitor externo.)
    const papel: string = profile?.role ?? '';
    const podeRegistrarInterrupcao = !!profile && papel !== 'geradoras_externo'
        && (papel === 'adm' || papel === 'gerente' || !!profile.permissions?.includes('modulo:geradoras'));

    const [unidades, setUnidades] = useState<UnidadeGeradora[]>([]);
    const [metas, setMetas] = useState<MetaGeradoraRow[]>([]);
    const [serie, setSerie] = useState<PontoSerie[]>([]);
    const [serieContraste, setSerieContraste] = useState<PontoSerie[]>([]);
    const [resumoA, setResumoA] = useState<ResumoGeracaoUsina[]>([]);
    const [resumoB, setResumoB] = useState<ResumoGeracaoUsina[]>([]);
    // Mesma fonte do cron que notifica adm/gerente: tela e notificacao nunca discordam.
    const [paradas, setParadas] = useState<GeradoraSemReportar[]>([]);
    const [vsMeta, setVsMeta] = useState<PontoVsMetaRow[]>([]);
    /**
     * O painel de meta tem período PRÓPRIO, desacoplado do seletor da curva.
     * A meta do laudo é mensal e fortemente sazonal (fev 139 vs ago 174
     * kWh/kWp em Morrinhos): lida dentro do recorte padrão da curva — 7 dias —
     * ela vira um ponto só e não responde nada. Aqui a pergunta é do ano.
     */
    const [presetMeta, setPresetMeta] = useState<PresetPeriodo>('ano_atual');

    const [site, setSite] = useState<string>(siteDaUrl);
    const [preset, setPreset] = useState<PresetPeriodo>('7d');
    const [granManual, setGranManual] = useState<Granularidade | null>(null);
    const [comparacao, setComparacao] = useState<ModoComparacao>('anterior');
    const [referencia, setReferencia] = useState<ModoReferencia>('irmas');

    const [carregando, setCarregando] = useState(true);
    const [erro, setErro] = useState<string | null>(null);

    const janela = useMemo(() => janelaDoPreset(preset), [preset]);
    const janelaB = useMemo(() => janelaComparacao(janela, comparacao), [janela, comparacao]);
    const granularidade = granManual ?? granularidadeSugerida(janela);

    const sites = useMemo(() => unidades.map(u => u.site_id_api), [unidades]);
    const sitesDaConsulta = useMemo(() => (site === TODAS ? sites : [site]), [site, sites]);

    // -------- carga de cadastro (uma vez) --------
    useEffect(() => {
        Promise.all([
            api.geradoras.getUnidades(),
            api.geradoras.getMetas().catch(() => []),
            api.geradoras.getUsinasParadas().catch(() => [] as GeradoraSemReportar[]),
        ])
            .then(([u, m, pd]) => {
                setUnidades(u);
                // `?site=` com usina que não existe mais (ou id digitado errado):
                // volta para "Todas" em vez de consultar um site_id fantasma e
                // mostrar a tela vazia sem dizer por quê.
                setSite(atual => (atual === TODAS || u.some(x => x.site_id_api === atual) ? atual : TODAS));
                setMetas(m as MetaGeradoraRow[]);
                setParadas(pd as GeradoraSemReportar[]);
            })
            .catch(e => setErro(e?.message || 'Falha ao carregar as usinas'));
    }, []);

    // -------- carga das séries --------
    const carregar = useCallback(async () => {
        if (sites.length === 0) return;
        setCarregando(true);
        setErro(null);
        try {
            // "Todas" usa a RPC que soma no banco: 1 linha por balde em vez de
            // 15. O ranking sempre pede o resumo de TODAS, porque a régua
            // "usinas irmãs" precisa enxergar o conjunto mesmo quando o
            // gráfico está numa usina só.
            const serieA = site === TODAS
                ? api.geradoras.getSerieGeracaoTotal(sitesDaConsulta, janela.inicio, janela.fim, granularidade)
                : api.geradoras.getSerieGeracao(sitesDaConsulta, janela.inicio, janela.fim, granularidade);

            const [sA, sB, rA, rB] = await Promise.all([
                serieA,
                janelaB
                    ? (site === TODAS
                        ? api.geradoras.getSerieGeracaoTotal(sitesDaConsulta, janelaB.inicio, janelaB.fim, granularidade)
                        : api.geradoras.getSerieGeracao(sitesDaConsulta, janelaB.inicio, janelaB.fim, granularidade))
                    : Promise.resolve([] as PontoSerie[]),
                api.geradoras.getResumoGeracao(sites, janela.inicio, janela.fim),
                janelaB
                    ? api.geradoras.getResumoGeracao(sites, janelaB.inicio, janelaB.fim)
                    : Promise.resolve([] as ResumoGeracaoUsina[]),
            ]);
            setSerie(sA); setSerieContraste(sB); setResumoA(rA); setResumoB(rB);
        } catch (e: any) {
            setErro(e?.message || 'Falha ao carregar a série de geração');
        } finally {
            setCarregando(false);
        }
    }, [sites, sitesDaConsulta, site, janela, janelaB, granularidade]);

    useEffect(() => { carregar(); }, [carregar]);

    // -------- carga do esperado x realizado (recorte próprio) --------
    const janelaMeta = useMemo(() => janelaDoPreset(presetMeta), [presetMeta]);

    useEffect(() => {
        if (sitesDaConsulta.length === 0) return;
        let vivo = true;
        api.geradoras
            .getGeracaoVsMeta(sitesDaConsulta, janelaMeta.inicio, janelaMeta.fim)
            // Falha aqui não derruba a tela: o painel some e a curva continua.
            .catch(() => [] as PontoVsMetaRow[])
            .then(v => { if (vivo) setVsMeta(v); });
        return () => { vivo = false; };
    }, [sitesDaConsulta, janelaMeta]);

    // -------- derivações --------
    const porSite = useMemo(() => {
        const m: Record<string, UnidadeGeradora> = {};
        unidades.forEach(u => { m[u.site_id_api] = u; });
        return m;
    }, [unidades]);

    const capacidadeSelecionada = useMemo(() => {
        if (site !== TODAS) return porSite[site]?.capacidade_instalada ?? null;
        const soma = unidades.reduce((a, u) => a + (u.capacidade_instalada || 0), 0);
        return soma > 0 ? soma : null;
    }, [site, porSite, unidades]);

    const resumo = useMemo(() => resumirSerie(serie, capacidadeSelecionada), [serie, capacidadeSelecionada]);
    const resumoContraste = useMemo(() => resumirSerie(serieContraste, capacidadeSelecionada), [serieContraste, capacidadeSelecionada]);
    const comp = useMemo(() => compararResumos(resumo, resumoContraste), [resumo, resumoContraste]);

    const contrasteAlinhado = useMemo(
        () => (janelaB ? alinharPorPosicao(serie, serieContraste).map(x => x.b) : undefined),
        [janelaB, serie, serieContraste],
    );

    const medianaAmostras = useMemo(() => mediana(serie.map(p => p.amostras)), [serie]);

    const resumoVsMeta = useMemo(() => resumirVsMeta(vsMeta), [vsMeta]);

    /**
     * Proveniência da régua. Todas as metas de um mesmo laudo compartilham a
     * `fonte`; se um dia convivirem laudos diferentes na mesma seleção, mostra
     * quantos, em vez de escolher um e mentir sobre a origem.
     */
    const fonteDaMeta = useMemo(() => {
        const fontes = [...new Set(
            metas.filter(m => resumoVsMeta.sitesComMeta.includes(m.site_id_api) && m.fonte).map(m => m.fonte!),
        )];
        return fontes.length === 1 ? fontes[0] : fontes.length > 1 ? `${fontes.length} laudos` : null;
    }, [metas, resumoVsMeta.sitesComMeta]);

    // A régua 'meta' do ranking precisa da capacidade DO CADASTRO para converter
    // a meta do laudo à mesma base do realizado — ver metaNoPeriodo.
    const capacidadeKwpPorSite = useMemo(() => {
        const m: Record<string, number | null> = {};
        unidades.forEach(u => { m[u.site_id_api] = capacidadeKwp(u.capacidade_instalada); });
        return m;
    }, [unidades]);

    const diasDoPeriodo = useMemo(() => diasDaJanela(janela), [janela]);

    // Nome legível de cada bloco para a tabela "Por bloco" do painel de meta —
    // sem isto ela mostraria o site_id_api cru (hash de 24 caracteres da Chint).
    const nomePorSite = useMemo(() => {
        const m: Record<string, string> = {};
        unidades.forEach(u => {
            const d = getDisplayUsina(u);
            // Mesmo rótulo do seletor e do ranking: "Chint - Bloco 1 - 10036788950".
            m[u.site_id_api] = `${d.marca} · ${d.uc}`;
        });
        return m;
    }, [unidades]);

    const linhasRanking: LinhaRanking[] = useMemo(() => resumoA.map(r => {
        const u = porSite[r.site_id_api];
        return {
            site_id_api: r.site_id_api,
            nome: u?.nome_usina ?? r.site_id_api,
            marca: u ? `${getDisplayUsina(u).marca} · ${getDisplayUsina(u).uc}` : r.site_id_api,
            kwh: r.geracao_kwh,
            kwhKwp: kwhPorKwp(r.geracao_kwh, u?.capacidade_instalada),
            capacidadeMwp: u?.capacidade_instalada ?? null,
            diasComDado: r.dias_com_dado,
        };
    }), [resumoA, porSite]);

    const contrastePorSite = useMemo(() => {
        const m: Record<string, number | null> = {};
        resumoB.forEach(r => { m[r.site_id_api] = kwhPorKwp(r.geracao_kwh, porSite[r.site_id_api]?.capacidade_instalada); });
        return m;
    }, [resumoB, porSite]);

    // Sinais de dado ruim: sempre sobre a série por usina do período. Quando o
    // gráfico está em "todas", a série somada não serve — usa o resumo.
    const sinais = useMemo(() => {
        if (site !== TODAS) return detectarSinais(serie, granularidade);
        return resumoA
            .filter(r => (r.geracao_kwh ?? 0) === 0 || r.dias_com_dado === 0)
            .map(r => ({
                site_id_api: r.site_id_api,
                tipo: (r.dias_com_dado === 0 ? 'sem_dado' : 'zerada') as 'sem_dado' | 'zerada',
                detalhe: r.dias_com_dado === 0
                    ? 'Nenhuma leitura no período'
                    : `Geração zero em ${r.dias_com_dado} dia(s) com leitura`,
                baldesAfetados: r.dias_com_dado,
            }));
    }, [site, serie, granularidade, resumoA]);

    const nomeSelecionado = site === TODAS
        ? `Todas as usinas (${unidades.length})`
        : porSite[site] ? `${getDisplayUsina(porSite[site]).marca} · ${getDisplayUsina(porSite[site]).uc}` : site;

    const opcoesUsina = useMemo(() => ([
        { v: TODAS, label: `Todas as usinas (${unidades.length})` },
        ...unidades.map(u => {
            const d = getDisplayUsina(u);
            return { v: u.site_id_api, label: `${d.marca} · ${d.uc}` };
        }),
    ]), [unidades]);

    /**
     * Cobertura parcial: a série do gráfico cobre menos do que o total do
     * período conhecido daquela usina.
     *
     * Acontece de verdade: as Qgbt3/4/5 (Canadian) só passaram a ter telemetria
     * contínua em 27/08/2026 — antes disso existe apenas o acumulado diário dos
     * snapshots. Pedir "ontem, hora a hora" para elas devolvia 2 baldes zerados
     * e o topo mostrava "0 kWh" enquanto o ranking, logo abaixo, mostrava 5.791
     * kWh do mesmo período. Os dois estavam certos e a tela se contradizia.
     *
     * Compara contra o resumo diário (que enxerga snapshot + telemetria) em vez
     * de testar `serie.length === 0`: pega tambem o caso geral de pedir periodo
     * anterior ao inicio da coleta de qualquer usina.
     */
    const coberturaParcial = useMemo(() => {
        if (site === TODAS) return null;
        const doRanking = resumoA.find(r => r.site_id_api === site)?.geracao_kwh ?? null;
        if (doRanking == null || doRanking <= 0) return null;
        if (resumo.totalKwh >= doRanking * 0.9) return null;
        return { noGrafico: resumo.totalKwh, noPeriodo: doRanking };
    }, [site, resumoA, resumo.totalKwh]);

    if (unidades.length === 0 && !erro) {
        return <LoadingSpinner label="Carregando usinas..." size="lg" />;
    }

    return (
        <div className="space-y-6 animate-fade-in pb-12">
            {/* Cabeçalho + controles */}
            <div className="flex flex-col xl:flex-row xl:items-start justify-between gap-4">
                <div>
                    <div className="flex items-center gap-3">
                        <h1 className="text-2xl font-poppins font-bold text-gray-900 dark:text-white">Análise de Geração</h1>
                        {carregando && <RefreshCw size={16} className="animate-spin text-primary" />}
                    </div>
                    <p className="text-gray-500 dark:text-gray-400 mt-1 text-sm">
                        {nomeSelecionado} · {janela.label}
                        {janelaB && <> · comparando com <span className="font-semibold">{janelaB.label}</span></>}
                    </p>
                </div>
            </div>

            <div className="flex flex-wrap gap-2.5">
                <Seletor<string> tag="Usina" icon={<Factory size={13} />} value={site} options={opcoesUsina} onChange={setSite} />
                <Seletor<PresetPeriodo>
                    tag="Período" icon={<Calendar size={13} />} value={preset}
                    options={(Object.keys(PRESET_LABEL) as PresetPeriodo[]).map(p => ({ v: p, label: PRESET_LABEL[p] }))}
                    onChange={p => { setPreset(p); setGranManual(null); }}
                />
                <Seletor<Granularidade>
                    tag="Granularidade" icon={<BarChart3 size={13} />} value={granularidade}
                    options={GRANULARIDADES} onChange={g => setGranManual(g)}
                />
                <Seletor<ModoComparacao> tag="Comparar com" icon={<GitCompareArrows size={13} />} value={comparacao} options={COMPARACOES} onChange={setComparacao} />
                <Seletor<ModoReferencia> tag="Régua do esperado" icon={<Ruler size={13} />} value={referencia} options={REFERENCIAS} onChange={setReferencia} />
            </div>

            {paradas.length > 0 && (
                <div className="rounded-2xl border border-rose-200 dark:border-rose-800 bg-rose-50/70 dark:bg-rose-900/20 px-5 py-4">
                    <div className="flex items-center gap-2 mb-2">
                        <TriangleAlert size={16} className="text-rose-600 dark:text-rose-400" />
                        <h3 className="font-poppins font-bold text-rose-800 dark:text-rose-300 text-sm">
                            {paradas.length} usina{paradas.length > 1 ? 's' : ''} exigindo ação
                        </h3>
                    </div>
                    <ul className="space-y-1.5">
                        {paradas.map(p => (
                            <li key={p.site_id_api} className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-xs">
                                <span className={`px-1.5 py-0.5 rounded-full font-bold uppercase tracking-wide text-[9px] border ${p.falha === 'sem_geracao'
                                    ? 'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 border-amber-300 dark:border-amber-700'
                                    : 'bg-rose-100 dark:bg-rose-900/40 text-rose-700 dark:text-rose-300 border-rose-300 dark:border-rose-700'}`}>
                                    {p.falha === 'sem_geracao' ? 'usina' : 'coletor'}
                                </span>
                                <span className="font-semibold text-gray-900 dark:text-white">{p.nome_usina}</span>
                                <span className="text-gray-600 dark:text-gray-400">{p.detalhe}</span>
                                {p.falha === 'sem_leitura' && p.horas_paradas != null && (
                                    <span className="text-rose-700 dark:text-rose-400 font-semibold tabular-nums">há {p.horas_paradas}h</span>
                                )}
                            </li>
                        ))}
                    </ul>
                    <p className="mt-2.5 text-[11px] text-rose-700/80 dark:text-rose-400/80">
                        Etiqueta <b>coletor</b> = falha de integração (TI). <b>usina</b> = equipamento parado (campo).
                        As mesmas condições disparam notificação a cada 30 min.
                    </p>
                </div>
            )}

            {erro && (
                <div className="rounded-2xl border border-rose-200 dark:border-rose-800 bg-rose-50 dark:bg-rose-900/20 px-4 py-3 text-sm text-rose-700 dark:text-rose-300 font-medium">
                    {erro}
                </div>
            )}

            {/* KPIs */}
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
                <Kpi
                    titulo="Geração no período" tom="amber" icone={<Sun size={22} />}
                    valor={formatKwh(resumo.totalKwh).split(' ')[0]}
                    unidade={formatKwh(resumo.totalKwh).split(' ')[1]}
                    rodape={
                        janelaB
                            ? comp.comparavel
                                ? <span className={comp.deltaPct! >= 0 ? 'text-emerald-600 dark:text-emerald-400 font-bold' : 'text-rose-600 dark:text-rose-400 font-bold'}>
                                    {comp.deltaPct! >= 0 ? '▲' : '▼'} {Math.abs(comp.deltaPct!).toFixed(1)}% vs {janelaB.label}
                                </span>
                                : <span className="text-gray-400">Sem histórico em {janelaB.label} para comparar</span>
                            : `${resumo.baldesComDado} ${granularidade === 'hora' ? 'horas' : granularidade === 'dia' ? 'dias' : 'meses'} com dado`
                    }
                />
                <Kpi
                    titulo="Rendimento" tom="violet" icone={<Scale size={22} />}
                    valor={resumo.kwhKwp != null ? resumo.kwhKwp.toLocaleString('pt-BR', { maximumFractionDigits: 2 }) : '—'}
                    unidade="kWh/kWp"
                    rodape={capacidadeSelecionada
                        ? `Sobre ${capacidadeSelecionada.toLocaleString('pt-BR', { maximumFractionDigits: 2 })} MWp instalados`
                        : 'Capacidade não cadastrada'}
                />
                <Kpi
                    titulo="Pico de potência" tom="blue" icone={<Gauge size={22} />}
                    valor={formatPower(resumo.picoPotenciaW).split(' ')[0]}
                    unidade={formatPower(resumo.picoPotenciaW).split(' ')[1]}
                    rodape={resumo.melhor
                        ? `Melhor ${granularidade === 'hora' ? 'hora' : granularidade === 'dia' ? 'dia' : 'mês'}: ${rotuloBucket(resumo.melhor.bucket, granularidade)} (${formatKwh(resumo.melhor.kwh)})`
                        : 'Sem leitura de potência'}
                />
                <Kpi
                    titulo={`Média por ${granularidade === 'hora' ? 'hora' : granularidade === 'dia' ? 'dia' : 'mês'}`}
                    tom="emerald" icone={<Zap size={22} />}
                    valor={resumo.mediaKwh != null ? formatKwh(resumo.mediaKwh).split(' ')[0] : '—'}
                    unidade={resumo.mediaKwh != null ? formatKwh(resumo.mediaKwh).split(' ')[1] : undefined}
                    rodape="Calculada só sobre baldes com leitura — buraco não vira queda"
                />
            </div>

            {/* Esperado x realizado (laudo PVsyst).
                Fica acima da curva de propósito: é a pergunta que a diretoria faz
                primeiro. A curva horária responde "o que aconteceu"; isto responde
                "e era para ter acontecido quanto?". */}
            <EsperadoRealizado
                resumo={resumoVsMeta}
                totalUsinasSelecionadas={site === TODAS ? unidades.length : 1}
                fonte={fonteDaMeta}
                periodoLabel={janelaMeta.label}
                preset={presetMeta}
                onTrocarPreset={setPresetMeta}
                nomePorSite={nomePorSite}
            />

            {/* Interrupções: o "por quê" do número acima. Segue o período e a
                usina do seletor, e busca por conta própria — a conta leva alguns
                segundos e não pode segurar a curva. */}
            <Interrupcoes
                unidades={unidades}
                site={site === TODAS ? null : site}
                janela={janela}
                geracao={resumoA}
                nomePorSite={nomePorSite}
                podeRegistrar={podeRegistrarInterrupcao}
            />

            {/* Curva principal */}
            <div className="bg-white dark:bg-gray-800 rounded-3xl border border-gray-100 dark:border-gray-700 shadow-sm p-5">
                <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
                    <div>
                        <h2 className="font-poppins font-bold text-gray-900 dark:text-white">
                            Potência e geração {granularidade === 'hora' ? 'hora a hora' : granularidade === 'dia' ? 'dia a dia' : 'mês a mês'}
                        </h2>
                        <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
                            Barras: energia gerada (kWh) · Linha: potência média (kW, eixo à direita)
                        </p>
                    </div>
                    {granularidade === 'hora' && medianaAmostras != null && medianaAmostras > 1 && (
                        <span className="inline-flex items-center gap-1.5 text-[11px] text-gray-400 dark:text-gray-500">
                            <Info size={12} />
                            barra clara = hora com menos de {Math.round(medianaAmostras / 2)} amostras
                        </span>
                    )}
                </div>

                {coberturaParcial && (
                    <div className="mb-4 rounded-2xl border border-amber-300 dark:border-amber-700 bg-amber-50/60 dark:bg-amber-900/15 px-4 py-3">
                        <div className="flex items-start gap-2">
                            <TriangleAlert size={15} className="text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
                            <div className="text-xs text-gray-700 dark:text-gray-300">
                                <b>Cobertura parcial nesta granularidade.</b> O gráfico soma {formatKwh(coberturaParcial.noGrafico)},
                                mas a usina gerou {formatKwh(coberturaParcial.noPeriodo)} no período — a telemetria contínua dela
                                não cobre a janela inteira.
                                {granularidade === 'hora' && <> Use <b>Por dia</b> para ver o período completo.</>}
                            </div>
                        </div>
                    </div>
                )}

                {(
                    <CurvaGeracao
                        pontos={serie}
                        contraste={contrasteAlinhado}
                        rotuloContraste={janelaB?.label}
                        granularidade={granularidade}
                        mediaKwh={resumo.mediaKwh}
                        medianaAmostras={medianaAmostras}
                        altura={340}
                    />
                )}
            </div>

            {/* Ranking + comparação */}
            <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
                <div className="xl:col-span-2">
                    <RankingUsinas
                        linhas={linhasRanking}
                        modo={referencia}
                        contrastePorSite={contrastePorSite}
                        metas={metas}
                        mesDeReferencia={janela.inicio.getMonth() + 1}
                        capacidadeKwpPorSite={capacidadeKwpPorSite}
                        diasDoPeriodo={diasDoPeriodo}
                        diasDoMesDeReferencia={diasNoMes(janela.inicio.getFullYear(), janela.inicio.getMonth() + 1)}
                        siteSelecionado={site === TODAS ? null : site}
                        onSelecionar={s => setSite(s === site ? TODAS : s)}
                    />
                </div>

                <div className="space-y-4">
                    {/* Comparação de períodos */}
                    <div className="bg-white dark:bg-gray-800 rounded-3xl border border-gray-100 dark:border-gray-700 shadow-sm p-5">
                        <h3 className="font-poppins font-bold text-gray-900 dark:text-white mb-3">Comparação de períodos</h3>
                        {!janelaB ? (
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                                Escolha um período de contraste em <b>Comparar com</b>.
                            </p>
                        ) : (
                            <div className="space-y-2.5 text-sm">
                                {[
                                    { rot: janela.label, res: resumo, destaque: true },
                                    { rot: janelaB.label, res: resumoContraste, destaque: false },
                                ].map(l => (
                                    <div key={l.rot} className={`rounded-2xl px-4 py-3 border ${l.destaque
                                        ? 'border-amber-200 dark:border-amber-800 bg-amber-50/60 dark:bg-amber-900/15'
                                        : 'border-gray-100 dark:border-gray-700 bg-gray-50/70 dark:bg-gray-900/30'}`}>
                                        <div className="text-[10px] uppercase tracking-wider font-bold text-gray-500 dark:text-gray-400">{l.rot}</div>
                                        <div className="text-xl font-bold text-gray-900 dark:text-white tabular-nums mt-0.5">{formatKwh(l.res.totalKwh)}</div>
                                        <div className="text-[11px] text-gray-500 dark:text-gray-400 tabular-nums mt-0.5">
                                            {l.res.baldesComDado > 0
                                                ? <>média {formatKwh(l.res.mediaKwh)} · melhor {l.res.melhor ? formatKwh(l.res.melhor.kwh) : '—'}</>
                                                : 'sem leitura no período'}
                                        </div>
                                    </div>
                                ))}
                                <div className="pt-1 text-center">
                                    {comp.comparavel ? (
                                        <>
                                            <div className={`text-2xl font-bold tabular-nums ${comp.deltaPct! >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>
                                                {comp.deltaPct! >= 0 ? '+' : ''}{comp.deltaPct!.toFixed(1)}%
                                            </div>
                                            <div className="text-[11px] text-gray-500 dark:text-gray-400 tabular-nums">
                                                {comp.deltaKwh >= 0 ? '+' : ''}{formatKwh(Math.abs(comp.deltaKwh))} de diferença
                                            </div>
                                        </>
                                    ) : (
                                        <div className="text-xs text-gray-400 dark:text-gray-500 px-2">
                                            Sem cobertura em {janelaB.label} — não é queda, é ausência de histórico.
                                            {comparacao === 'ano_anterior' && ' A telemetria contínua começou em 2026 para quase todas as usinas.'}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Qualidade do dado */}
                    <div className="bg-white dark:bg-gray-800 rounded-3xl border border-gray-100 dark:border-gray-700 shadow-sm p-5">
                        <h3 className="font-poppins font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                            <TriangleAlert size={16} className={sinais.length ? 'text-amber-500' : 'text-gray-300 dark:text-gray-600'} />
                            Sinais no dado
                        </h3>
                        {sinais.length === 0 ? (
                            <p className="text-sm text-gray-500 dark:text-gray-400">Nenhuma inconsistência no período.</p>
                        ) : (
                            <ul className="space-y-2">
                                {sinais.map((s, i) => {
                                    const u = porSite[s.site_id_api];
                                    return (
                                        <li key={`${s.site_id_api}-${s.tipo}-${i}`} className="rounded-xl border border-amber-200 dark:border-amber-800 bg-amber-50/60 dark:bg-amber-900/15 px-3 py-2">
                                            <div className="text-xs font-bold text-gray-800 dark:text-gray-200">
                                                {u ? `${getDisplayUsina(u).marca} · ${getDisplayUsina(u).uc}` : s.site_id_api}
                                            </div>
                                            <div className="text-[11px] text-amber-700 dark:text-amber-300 mt-0.5">{s.detalhe}</div>
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AnaliseView;
